import React, { useContext, useEffect, useState } from 'react'
import ErrorMessage from 'app/React/Components/misc/ErrorMessage'
import { getCurrentPrice } from 'app/React/Helpers/PriceHelper'
import Tip from 'app/React/Components/misc/Tip'
import PropTypes from 'prop-types'
import { ConfiguratorContext } from "app/React/ConfiguratorContainer";

function RangeSlider({ marks, value, onChange }) {
  const currentIndex = Math.max(0, marks.findIndex((mark) => mark.value === value))
  const maxIndex = Math.max(0, marks.length - 1)
  const progress = maxIndex === 0 ? 0 : (currentIndex / maxIndex) * 100

  return (
    <div className='mt-slider'>
      <input
        type='range'
        min={0}
        max={maxIndex}
        step={1}
        value={currentIndex}
        style={{ '--slider-progress': `${progress}%` }}
        onChange={(event) => onChange(marks[parseInt(event.target.value, 10)]?.value)}
      />
      <div className='mt-slider__labels'>
        <span>{marks[0]?.label}</span>
        <span>{marks[maxIndex]?.label}</span>
      </div>
    </div>
  )
}

export default function LeasingKmField(props) {
  const {
    cartItems,
    currentEngineTransmission,
    currentLeasingPriceRanges,
    currentVariant,
    lang,
    order,
    strings,
    setCurrentLeasingPriceRanges,
    setOrder
  } = useContext(ConfiguratorContext)

  const paymentFromStrings = strings.payment_form ?? {}
  const priceStrings = strings.price?.formats ?? {}

  const step1_tip_leasing_range = currentVariant.step1_tip_leasing_range ?? []
  const leasing_price_range = order.leasing_price_range ?? null

  const [currentLeasingDeposit, setCurrentLeasingDeposit] = useState([])
  const [currentLeasingKm, setCurrentLeasingKm] = useState([])

  const formatCurrentLeasingDeposit = () => {
    const newLeasingPriceDeposits = currentEngineTransmission.leasingPriceRanges
      .map(leasingPriceRange => {
        return {
          value: parseInt(leasingPriceRange[0].security_deposit),
          label: (new Intl.NumberFormat(lang).format(leasingPriceRange[0].security_deposit)) + ' ' + priceStrings.cash
        }
      })

    setCurrentLeasingDeposit(newLeasingPriceDeposits)
  }

  const formatCurrentLeasingKm = () => {
    const newLeasingDepositKm = currentLeasingPriceRanges.map(currentLeasingPriceRange => {
      return {
        value: parseInt(currentLeasingPriceRange.from_km),
        label: (new Intl.NumberFormat(lang).format(currentLeasingPriceRange.from_km)) + ' ' + paymentFromStrings.leasing_range.km
      }
    })

    setCurrentLeasingKm(newLeasingDepositKm)
  }

  useEffect(() => {
    const currentLeasingPriceRange = order.leasing_price_range
    if (currentLeasingPriceRange !== null) {
      return
    }

    const currentLeasingPriceRanges = currentEngineTransmission?.leasingPriceRanges?.[0] ?? []
    setCurrentLeasingPriceRanges([ ...currentLeasingPriceRanges ])

    order.leasing_price_range = currentLeasingPriceRanges[0] ?? {}
    setOrder({ ...order })
  }, [])

  useEffect(() => {
    const currentLeasingPriceRange = order.leasing_price_range
    if (currentLeasingPriceRange === null) {
      order.leasing_price_range = currentLeasingPriceRanges[0] ?? {}
      setOrder({ ...order })
    }

    formatCurrentLeasingDeposit()
    formatCurrentLeasingKm()
  }, [currentVariant, currentEngineTransmission, leasing_price_range])

  if (currentLeasingPriceRanges.length === 0 || currentLeasingKm.length === 0) return null

  const updateOrder = (leasingPriceRange) => {
    order.leasing_price_range = leasingPriceRange
    order.price = getCurrentPrice(order.paymentType, order, currentVariant, cartItems)
    setOrder({ ...order })
  }

  const onChangeDeposit = (newValue) => {
    const _leasingPriceRanges = currentEngineTransmission.leasingPriceRanges.find(leasingPriceRanges => leasingPriceRanges[0].security_deposit == newValue)

    setCurrentLeasingPriceRanges([ ..._leasingPriceRanges ])
    formatCurrentLeasingKm()

    const _leasingPriceRangeMatchFromKm = _leasingPriceRanges.find(leasingPriceRange => leasingPriceRange.from_km == leasing_price_range.from_km)

    if (_leasingPriceRangeMatchFromKm !== undefined) {
      updateOrder(_leasingPriceRangeMatchFromKm)
      return
    }

    updateOrder(_leasingPriceRanges[0])
  }

  const onChangeKm = (newValue) => {
    const currentSecurityDeposit = leasing_price_range.security_deposit
    const _leasingPriceRange = currentLeasingPriceRanges.find(leasingPriceRange => leasingPriceRange.security_deposit === currentSecurityDeposit && parseInt(leasingPriceRange.from_km) === newValue)

    if (_leasingPriceRange !== undefined) {
      updateOrder(_leasingPriceRange)
    }
  }

  return (
    <>
      {
        currentLeasingDeposit.length > 0 && (
          <div className='setting'>
            <div className='config-wrapper'>
              <h2 className='config-subtitle'>
                {paymentFromStrings.leasing_deposit.label}
                {typeof step1_tip_leasing_range === 'string' && step1_tip_leasing_range.length > 0 ? (
                  <Tip content={step1_tip_leasing_range}/>
                ) : null}
              </h2>
              <div className='config-tag'>
                {new Intl.NumberFormat(lang).format(leasing_price_range.security_deposit)}
                {' '}
                {priceStrings.cash}
              </div>
            </div>
            {
              currentLeasingDeposit.length > 1 && (
                <div className='config-radio'>
                  <RangeSlider
                    marks={currentLeasingDeposit}
                    value={parseInt(leasing_price_range.security_deposit)}
                    onChange={onChangeDeposit}
                  />
                </div>
              )
            }
            <ErrorMessage field='leasing_deposit'/>
            <div className='config-seperator'/>
          </div>
        )
      }

      {
        currentLeasingKm.length > 0 && (
          <div className='setting'>
            <div className='config-wrapper'>
              <h2 className='config-subtitle'>
                {paymentFromStrings.leasing_range.label}
              </h2>
              <div className='config-tag'>
                {new Intl.NumberFormat(lang).format(leasing_price_range.from_km)}
                {' '}
                {paymentFromStrings.leasing_range.km}
              </div>
            </div>
            {
              currentLeasingKm.length > 1 && (
                <div className='config-radio'>
                  <RangeSlider
                    marks={currentLeasingKm}
                    value={parseInt(leasing_price_range.from_km)}
                    onChange={onChangeKm}
                  />
                </div>
              )
            }
            <ErrorMessage field='leasing_range'/>
            <div className='config-seperator'/>
          </div>
        )}
    </>
  )
}

LeasingKmField.propTypes = {
  tip: PropTypes.any
}
